import { Organization } from '@models/Organization';
import { PilotOnboardingService, PilotOrganizationSetup, PilotSourceOption } from '@services/PilotOnboardingService';
import { pilotSourceOptions } from './PilotSourceOptions';
import { UsersService } from '@services/UsersService';
import { HollowedButton, NormalButton } from '@shared/designSystem/Button';
import { ComboBox, ComboBoxOption } from '@shared/designSystem/ComboBox';
import { CardContainer, ColumnsContainer, HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { Modal } from '@shared/designSystem/messages/Modal';
import { TextInput, TextInputRef } from '@shared/designSystem/TextInput';
import { BoldText, SmallText, Text } from '@shared/designSystem/Texts';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { useEffect, useRef } from 'react';
import { usePilotOnboardingStartModalViewModel } from './PilotOnboardingStartModalViewModel';

const organizationSetupOptions: ComboBoxOption[] = [
    { id: 'presenteeismAndPayslips', title: 'Presenteeism + recibos de sueldo'.localized() },
    { id: 'presenteeism', title: 'Solo presenteeism'.localized() },
    { id: 'payslips', title: 'Solo recibos de sueldo'.localized() },
];

interface PilotOnboardingStartModalScreenProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => Promise<void> | void;
    org: Organization;
    pilotOnboardingService: PilotOnboardingService;
    usersService: UsersService;
}

const PilotOnboardingStartModalScreen: React.FC<PilotOnboardingStartModalScreenProps> = ({
    isOpen,
    onClose,
    onSuccess,
    org,
    pilotOnboardingService,
    usersService,
}) => {
    const viewModel = usePilotOnboardingStartModalViewModel({
        currentOrganization: org,
        isOpen,
        pilotOnboardingService,
        usersService,
    });
    const organizationIDInputRef = useRef<TextInputRef>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            organizationIDInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [isOpen]);

    const latestResultText = viewModel.latestSubmissionResult?.kind === 'error'
        ? viewModel.latestSubmissionResult.errorMessage
        : undefined;

    const handleSubmit = async () => {
        const result = await viewModel.submit();

        if (!result) {
            return;
        }

        if (result.kind === 'success') {
            onClose();
            ToastManager.shared().showSuccessMessage(result.message || 'Piloto creado correctamente.'.localized());
            void onSuccess?.();
            return;
        }

        ToastManager.shared().showErrorMessage(result.errorMessage ?? 'No se pudo crear el piloto. Intenta nuevamente.'.localized());
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Crear piloto".localized()}
            width="760px"
            maxWidth="96vw"
            maxHeight="90vh"
        >
            <VerticalContainer height="100%" gap={designSystemStyles.gapMd}>
                <HorizontalContainer alignment="start" gap={designSystemStyles.gapLg} width="100%">
                    <VerticalContainer gap={designSystemStyles.gapSm} width="100%">
                        <HorizontalContainer width="100%" justifyContent="space-between" alignment="end">
                            <VerticalContainer width="220px" margin="0">
                                <TextInput
                                    ref={organizationIDInputRef}
                                    label={"ID de organizacion".localized()}
                                    value={viewModel.organizationID}
                                    onChange={(value) => viewModel.setOrganizationID(value.toUpperCase())}
                                    placeholder={"PILOT001".localized()}
                                    width="220px"
                                />
                            </VerticalContainer>
                            <VerticalContainer width="120px" margin="0">
                                <TextInput
                                    label={"Pais".localized()}
                                    value={viewModel.countryCode}
                                    onChange={(value) => viewModel.setCountryCode(value.toUpperCase().slice(0, 2))}
                                    placeholder="AR"
                                    width="120px"
                                />
                            </VerticalContainer>
                        </HorizontalContainer>
                        <VerticalContainer gap={designSystemStyles.gapXxxSm} width="100%" margin="0">
                            <SmallText text={"Tipo de organizacion".localized()} />
                            <ComboBox
                                options={organizationSetupOptions}
                                value={organizationSetupOptions.find((option) => option.id === viewModel.organizationSetup)}
                                onChange={(option) => viewModel.setOrganizationSetup(option.id as PilotOrganizationSetup)}
                                iconType={null}
                            />
                        </VerticalContainer>
                        <VerticalContainer gap={designSystemStyles.gapXxxSm} width="100%" margin="0">
                            <SmallText text={"Origen del piloto".localized()} />
                            <ComboBox
                                options={pilotSourceOptions}
                                value={pilotSourceOptions.find((option) => option.id === viewModel.pilotSource)}
                                onChange={(option) => viewModel.setPilotSource(option.id as PilotSourceOption)}
                                placeholder={"Seleccionar origen".localized()}
                                iconType={null}
                            />
                        </VerticalContainer>
                        <TextInput
                            label={"Nombre".localized()}
                            value={viewModel.name}
                            onChange={viewModel.setName}
                            placeholder={"Nombre de la organizacion".localized()}
                        />
                        <HorizontalContainer gap={designSystemStyles.gapSm}>
                            <TextInput
                                label={"Email admin".localized()}
                                value={viewModel.adminEmail}
                                onChange={viewModel.setAdminEmail}
                                placeholder={"admin@empresa.com".localized()}
                                type="email"
                                inputMode="email"
                            />
                            <TextInput
                                label={"Nombre admin".localized()}
                                value={viewModel.adminName}
                                onChange={viewModel.setAdminName}
                                placeholder={"Administrador principal".localized()}
                            />
                        </HorizontalContainer>
                        {viewModel.includesQontactPoint && (
                            <>
                                <TextInput
                                    label={"Nombre del edificio".localized()}
                                    value={viewModel.buildingName}
                                    onChange={viewModel.setBuildingName}
                                    placeholder={"Edificio principal".localized()}
                                />
                                <HorizontalContainer gap={designSystemStyles.gapSm}>
                                    <TextInput
                                        label={"Latitud".localized()}
                                        value={viewModel.latitude}
                                        onChange={viewModel.setLatitude}
                                        placeholder="-34.52010791066458"
                                    />
                                    <TextInput
                                        label={"Longitud".localized()}
                                        value={viewModel.longitude}
                                        onChange={viewModel.setLongitude}
                                        placeholder="-58.47592258972071"
                                    />
                                </HorizontalContainer>
                            </>
                        )}
                        <SmallText text={viewModel.includesQontactPoint
                            ? "Este flujo crea la organizacion piloto y su primer Qontact Point.".localized()
                            : "Este flujo crea la organizacion piloto con la entrega de recibos por WhatsApp, sin Qontact Point de presentismo.".localized()} />
                        {viewModel.latestSubmissionResult?.kind === 'error' && latestResultText && (
                            <VerticalContainer
                                gap={designSystemStyles.gapXxxSm}
                                padding={designSystemStyles.paddingSm}
                                backgroundColor="#FFF3F1"
                                borderRadius={designSystemStyles.cornerRadiusComponent}
                            >
                                <SmallText text={'Ultimo error'.localized()} />
                                <Text text={latestResultText} />
                            </VerticalContainer>
                        )}
                    </VerticalContainer>
                </HorizontalContainer>

                <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                    <HollowedButton
                        text={"Cerrar".localized()}
                        onClick={onClose}
                        width="160px"
                    />
                    <NormalButton
                        text={"Crear piloto".localized()}
                        onClick={() => void handleSubmit()}
                        disabled={!viewModel.canSubmit}
                        loading={viewModel.isSubmitting}
                        width="200px"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

export default PilotOnboardingStartModalScreen;
